import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Search, X } from 'lucide-react';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { track } from '../../util/analytics';
import { buildSearchTarget } from '../../util/unifiedSearch';

import { keywordSearch, runConciergeSearch } from './conciergeSearch';
import { useListingCount } from './useListingCount';
import SpaceTypeOptions from './SpaceTypeOptions';
import WhereField, { KIND_AI, whereLabel } from './WhereField';
import WhenField, { whenLabel } from './WhenField';

import css from './SearchEditor.module.css';

export const MODE_BAR = 'bar';
export const MODE_DIALOG = 'dialog';

const SECTION_WHERE = 'where';
const SECTION_TYPE = 'type';
const SECTION_WHEN = 'when';

const draftOf = (state = {}) => ({
  where: state.where || null,
  spaceType: state.spaceType || null,
  dates: state.dates || null,
});

const withoutKeywords = (state = {}) => {
  // eslint-disable-next-line no-unused-vars
  const { keywords, ...filters } = state.filters || {};
  return { ...state, filters };
};

/**
 * The editor of the unified search (spec §4.1, §4.3): a draft `{ where, spaceType, dates }` taken
 * from the URL each time it opens, and exactly one primary button that applies it.
 *
 * - `bar` (desktop ≥ 1024px): segments Wo | Raum | Wann and the button. The Raum and Wann
 *   segments open a popover under themselves; Escape and a click elsewhere close the popover, a
 *   closable bar (topbar) closes as a whole on the next Escape / outside click. Not a modal.
 * - `dialog` (< 1024px): content of the full-screen dialog — close button (focused on open),
 *   accordion Wo → Raum → Wann (a Wo pick advances to Raum), fixed bottom bar "Alles löschen" |
 *   button.
 *
 * Wann (since 2026-10-30, the owner's decision over spec §2): the /s dates filter as a third
 * field. With dates the target is always /s, and the live count stays "Suchen" — the API gives no
 * total for an availability query (countParamsForUrl).
 *
 * The button names the live count of the draft's target ("57 Angebote anzeigen") once it is
 * known and says "Suchen" before; it keeps a minimum width so the swap does not shift the bar,
 * and a polite live region announces the number.
 *
 * @component
 * @param {Object} props
 * @param {'bar'|'dialog'} props.mode
 * @param {string} props.idPrefix unique per instance
 * @param {Object} props.state readSearchState of the page
 * @param {Array} props.groups resolved space-type groups
 * @param {Function} props.onSubmit (target) — navigation is the caller's (push vs. replace)
 * @param {Function} [props.onRequestClose] close request (dialog ✕, closable bar Esc/outside)
 * @param {boolean} [props.closable] bar only: whether Esc / outside click close the bar
 * @param {boolean} [props.autoFocusWhere] focus the Wo input on mount
 * @param {boolean} [props.countsEnabled] whether live counts may query
 * @param {Function} [props.onInteract] first focus / pointer down inside the editor
 * @param {string} [props.resetKey] the draft is re-read from `state` whenever this changes
 * @param {string} [props.titleId] dialog only: id of the dialog's name
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const SearchEditor = props => {
  const {
    mode,
    idPrefix,
    state,
    groups,
    onSubmit,
    onRequestClose,
    closable = false,
    autoFocusWhere = false,
    countsEnabled = false,
    onInteract,
    resetKey,
    titleId,
    className,
  } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const isBar = mode === MODE_BAR;

  const [draft, setDraft] = useState(() => draftOf(state));
  const [typeOpen, setTypeOpen] = useState(false);
  const [whenOpen, setWhenOpen] = useState(false);
  const [section, setSection] = useState(SECTION_WHERE);
  const [busy, setBusy] = useState(false);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const barRef = useRef(null);
  const typeSegmentRef = useRef(null);
  const typeButtonRef = useRef(null);
  const typePopoverRef = useRef(null);
  const whenSegmentRef = useRef(null);
  const whenButtonRef = useRef(null);
  const whereRef = useRef(null);
  const closeRef = useRef(null);
  const focusCheckedTypeRef = useRef(false);
  const isMountedRef = useRef(false);
  const isFirstResetRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isFirstResetRef.current) {
      isFirstResetRef.current = false;
      return;
    }
    setDraft(draftOf(state));
    setTypeOpen(false);
    setWhenOpen(false);
    setSection(SECTION_WHERE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Dialog: focus goes to ✕ (Airbnb pattern, apple-hig-web §7). Bar from the topbar: to Wo.
  useEffect(() => {
    if (!isBar) {
      closeRef.current?.focus({ preventScroll: true });
    } else if (autoFocusWhere) {
      whereRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-advance from Wo: the checked space type takes the focus once its popover is open.
  useEffect(() => {
    if (typeOpen && focusCheckedTypeRef.current) {
      focusCheckedTypeRef.current = false;
      typePopoverRef.current?.querySelector('[aria-checked="true"]')?.focus();
    }
  }, [typeOpen]);

  // Bar: a press outside the Raum / Wann segment closes its popover; outside the bar closes a
  // closable bar.
  useEffect(() => {
    if (!isBar || (!typeOpen && !whenOpen && !closable)) {
      return undefined;
    }
    const handlePointerDown = event => {
      const target = event.target;
      if (typeOpen && typeSegmentRef.current && !typeSegmentRef.current.contains(target)) {
        setTypeOpen(false);
      }
      if (whenOpen && whenSegmentRef.current && !whenSegmentRef.current.contains(target)) {
        setWhenOpen(false);
      }
      if (closable && barRef.current && !barRef.current.contains(target) && onRequestClose) {
        onRequestClose();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isBar, typeOpen, whenOpen, closable, onRequestClose]);

  const target = useMemo(() => buildSearchTarget({ draft, current: state, groups }), [
    draft,
    state,
    groups,
  ]);
  const count = useListingCount(target, { enabled: countsEnabled });
  const hasCount = typeof count === 'number';

  // ---- Applying ---- //
  const submitTarget = (url, resultCount) => {
    track.ctaClick({
      click_id: 'search_submit',
      click_text: url.startsWith('/coworking/') ? 'city' : 'search',
      click_url: url,
      ...(typeof resultCount === 'number' ? { results: resultCount } : {}),
    });
    onSubmit(url);
  };

  const advanceToType = () => {
    if (isBar) {
      focusCheckedTypeRef.current = true;
      setWhenOpen(false);
      setTypeOpen(true);
    } else {
      setSection(SECTION_TYPE);
    }
  };

  const handleSelectWhere = (where, kind, meta = {}) => {
    const nextDraft = { ...draftRef.current, where };
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    if (!kind) {
      return; // the field's clear button: no pick, no advance
    }
    track.ctaClick({ click_id: 'search_where_select', click_text: kind });
    if (meta.submit) {
      submitTarget(buildSearchTarget({ draft: nextDraft, current: state, groups }));
      return;
    }
    advanceToType();
  };

  const navigateWithResult = (result, { keepWhere }) => {
    const current = draftRef.current;
    const where = keepWhere ? result.where || current.where : null;
    const spaceType = result.spaceType === undefined ? current.spaceType : result.spaceType;
    submitTarget(
      buildSearchTarget({
        // The dates of the draft travel with a keyword or KI search too (review 2026-11-02).
        draft: { where, spaceType, dates: current.dates },
        current: withoutKeywords(state),
        groups,
        extraParams: result.extraParams,
      })
    );
  };

  const handleSearchText = async (kind, text) => {
    if (busy) {
      return;
    }
    track.ctaClick({ click_id: 'search_where_select', click_text: kind });
    if (kind !== KIND_AI) {
      // A keyword search drops the old place: Lugano words inside Zürich bounds find nothing.
      navigateWithResult(keywordSearch(text), { keepWhere: false });
      return;
    }
    setBusy(true);
    const result = await runConciergeSearch({ text, config, locale: intl.locale, groups });
    if (!isMountedRef.current) {
      return;
    }
    setBusy(false);
    navigateWithResult(result, { keepWhere: !result.fallback });
  };

  const handleFormSubmit = event => {
    event.preventDefault();
    if (busy) {
      return;
    }
    if (whereRef.current?.commitPending()) {
      return;
    }
    submitTarget(target, count);
  };

  const handleTypeChange = (key, { pick }) => {
    const nextDraft = { ...draftRef.current, spaceType: key };
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    if (isBar && pick) {
      setTypeOpen(false);
      typeButtonRef.current?.focus();
    }
  };

  const handleWhenChange = (dates, { pick }) => {
    const nextDraft = { ...draftRef.current, dates };
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    if (isBar && pick) {
      setWhenOpen(false);
      whenButtonRef.current?.focus();
    }
  };

  const toggleWhen = () => {
    setTypeOpen(false);
    setWhenOpen(open => !open);
  };

  const handleClearAll = () => {
    const nextDraft = { where: null, spaceType: null, dates: null };
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    setSection(SECTION_WHERE);
  };

  const handleBarKeyDown = event => {
    if (event.key !== 'Escape' || event.defaultPrevented) {
      return;
    }
    if (typeOpen) {
      event.preventDefault();
      setTypeOpen(false);
      typeButtonRef.current?.focus();
    } else if (whenOpen) {
      event.preventDefault();
      setWhenOpen(false);
      whenButtonRef.current?.focus();
    } else if (closable && onRequestClose) {
      event.preventDefault();
      onRequestClose();
    }
  };

  // ---- Pieces ---- //
  const submitLabel = hasCount
    ? intl.formatMessage({ id: 'UnifiedSearch.showResults' }, { count })
    : intl.formatMessage({ id: 'UnifiedSearch.submit' });
  const statusText = busy
    ? intl.formatMessage({ id: 'UnifiedSearch.thinking' })
    : hasCount
    ? submitLabel
    : '';
  const typeLabel = intl.formatMessage({
    id: draft.spaceType ? `UnifiedSearch.type.${draft.spaceType}` : 'UnifiedSearch.type.all',
  });
  const whereSummary =
    whereLabel(draft.where, intl) || intl.formatMessage({ id: 'UnifiedSearch.where.anywhere' });
  const whenSummary =
    whenLabel(draft.dates, intl) || intl.formatMessage({ id: 'UnifiedSearch.when.any' });

  const submitButton = (
    <button
      type="submit"
      className={css.submit}
      disabled={busy}
      data-testid="unified-search-submit"
    >
      <span className={css.submitIcon} aria-hidden="true">
        <Search size={18} strokeWidth={2.25} />
      </span>
      <span className={css.submitLabel}>{submitLabel}</span>
    </button>
  );
  const status = (
    <span className={busy ? css.thinking : css.srOnly} role="status" aria-live="polite">
      {statusText}
    </span>
  );

  const whereField = (
    <WhereField
      ref={whereRef}
      id={`${idPrefix}-where`}
      where={draft.where}
      onSelectWhere={handleSelectWhere}
      onSearchText={handleSearchText}
      inline={!isBar}
      countsEnabled={countsEnabled}
      busy={busy}
      // The short placeholder everywhere: the example sentence was cut off in the bar segment and in
      // the 343px dialog field alike (measured 2026-10-18).
      placeholder={intl.formatMessage({ id: 'UnifiedSearch.where.placeholder' })}
    />
  );
  const typeOptions = (
    <SpaceTypeOptions
      id={`${idPrefix}-types`}
      groups={groups}
      value={draft.spaceType}
      onChange={handleTypeChange}
      where={draft.where}
      current={state}
      countsEnabled={countsEnabled}
    />
  );
  // The calendar mounts only while its popover / card is open: it is a lazy chunk and must never
  // be part of the server markup (the hero bar is open on the home page).
  const whenField = (
    <WhenField id={`${idPrefix}-when`} value={draft.dates} onChange={handleWhenChange} />
  );

  if (isBar) {
    const typePopoverId = `${idPrefix}-type-popover`;
    const whenPopoverId = `${idPrefix}-when-popover`;
    return (
      <form
        ref={barRef}
        className={classNames(css.bar, className)}
        role="search"
        aria-label={intl.formatMessage({ id: 'UnifiedSearch.dialogTitle' })}
        onSubmit={handleFormSubmit}
        onKeyDown={handleBarKeyDown}
        onFocusCapture={onInteract}
        onPointerDownCapture={onInteract}
        data-testid="unified-search-bar"
      >
        <div className={classNames(css.segment, css.segmentWhere)}>
          <label className={css.segmentLabel} htmlFor={`${idPrefix}-where-input`}>
            {intl.formatMessage({ id: 'UnifiedSearch.where.label' })}
          </label>
          {whereField}
        </div>
        <span className={css.segmentDivider} aria-hidden="true" />
        <div className={classNames(css.segment, css.segmentType)} ref={typeSegmentRef}>
          <button
            ref={typeButtonRef}
            type="button"
            className={css.typeButton}
            aria-expanded={typeOpen}
            aria-controls={typePopoverId}
            onClick={() => setTypeOpen(open => !open)}
            data-testid="unified-search-type-button"
          >
            <span className={css.segmentLabel}>
              {intl.formatMessage({ id: 'UnifiedSearch.type.label' })}
            </span>
            <span className={css.segmentValue}>{typeLabel}</span>
          </button>
          <div id={typePopoverId} ref={typePopoverRef} className={css.popover} hidden={!typeOpen}>
            {typeOptions}
          </div>
        </div>
        <span className={css.segmentDivider} aria-hidden="true" />
        <div className={classNames(css.segment, css.segmentWhen)} ref={whenSegmentRef}>
          <button
            ref={whenButtonRef}
            type="button"
            className={css.typeButton}
            aria-expanded={whenOpen}
            aria-controls={whenPopoverId}
            onClick={toggleWhen}
            data-testid="unified-search-when-button"
          >
            <span className={css.segmentLabel}>
              {intl.formatMessage({ id: 'UnifiedSearch.when.label' })}
            </span>
            <span className={classNames(css.segmentValue, { [css.segmentAny]: !draft.dates })}>
              {whenSummary}
            </span>
          </button>
          <div
            id={whenPopoverId}
            className={classNames(css.popover, css.popoverWhen)}
            hidden={!whenOpen}
            role="group"
            aria-label={intl.formatMessage({ id: 'UnifiedSearch.when.heading' })}
          >
            {whenOpen ? whenField : null}
          </div>
        </div>
        <div className={css.barSubmit}>{submitButton}</div>
        {status}
      </form>
    );
  }

  const wherePanelId = `${idPrefix}-where-panel`;
  const typePanelId = `${idPrefix}-type-panel`;
  const whenPanelId = `${idPrefix}-when-panel`;
  const isWhere = section === SECTION_WHERE;
  const isType = section === SECTION_TYPE;
  const isWhen = section === SECTION_WHEN;
  return (
    <div className={classNames(css.dialog, className)}>
      <div className={css.dialogHeader}>
        <h2 id={titleId} className={css.dialogTitle}>
          {intl.formatMessage({ id: 'UnifiedSearch.dialogTitle' })}
        </h2>
        <button
          ref={closeRef}
          type="button"
          className={css.close}
          aria-label={intl.formatMessage({ id: 'UnifiedSearch.close' })}
          onClick={onRequestClose}
          data-testid="unified-search-close"
        >
          <X size={20} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
      <form
        className={css.dialogForm}
        role="search"
        aria-labelledby={titleId}
        onSubmit={handleFormSubmit}
      >
        <div className={css.dialogBody}>
          <section className={classNames(css.card, { [css.cardOpen]: isWhere })}>
            <h3 className={css.cardHeading}>
              <button
                type="button"
                className={css.cardToggle}
                aria-expanded={isWhere}
                aria-controls={wherePanelId}
                onClick={() => setSection(SECTION_WHERE)}
              >
                <span className={css.cardLabel}>
                  {intl.formatMessage({ id: 'UnifiedSearch.where.label' })}
                </span>
                {isWhere ? null : <span className={css.cardValue}>{whereSummary}</span>}
              </button>
            </h3>
            <div id={wherePanelId} className={css.cardPanel} hidden={!isWhere}>
              {whereField}
            </div>
          </section>
          <section className={classNames(css.card, { [css.cardOpen]: isType })}>
            <h3 className={css.cardHeading}>
              <button
                type="button"
                className={css.cardToggle}
                aria-expanded={isType}
                aria-controls={typePanelId}
                onClick={() => setSection(SECTION_TYPE)}
                data-testid="unified-search-type-section"
              >
                <span className={css.cardLabel}>
                  {intl.formatMessage({ id: 'UnifiedSearch.type.label' })}
                </span>
                {isType ? null : <span className={css.cardValue}>{typeLabel}</span>}
              </button>
            </h3>
            <div id={typePanelId} className={css.cardPanel} hidden={!isType}>
              {typeOptions}
            </div>
          </section>
          <section className={classNames(css.card, { [css.cardOpen]: isWhen })}>
            <h3 className={css.cardHeading}>
              <button
                type="button"
                className={css.cardToggle}
                aria-expanded={isWhen}
                aria-controls={whenPanelId}
                onClick={() => setSection(SECTION_WHEN)}
                data-testid="unified-search-when-section"
              >
                <span className={css.cardLabel}>
                  {intl.formatMessage({ id: 'UnifiedSearch.when.label' })}
                </span>
                {isWhen ? null : (
                  <span className={classNames(css.cardValue, { [css.cardAny]: !draft.dates })}>
                    {whenSummary}
                  </span>
                )}
              </button>
            </h3>
            <div
              id={whenPanelId}
              className={classNames(css.cardPanel, css.cardPanelWhen)}
              hidden={!isWhen}
            >
              {isWhen ? whenField : null}
            </div>
          </section>
        </div>
        <div className={css.bottomBar}>
          <button
            type="button"
            className={css.clearAll}
            onClick={handleClearAll}
            data-testid="unified-search-clear"
          >
            {intl.formatMessage({ id: 'UnifiedSearch.clearAll' })}
          </button>
          {submitButton}
        </div>
        {status}
      </form>
    </div>
  );
};

export default SearchEditor;
